import { applySeo } from "@/lib/seo-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useServicesList } from "@/lib/services";
import { ArrowRight } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import heroOffice from "@/assets/hero-office.webp";

export const Route = createFileRoute("/services/areas")({
  component: Areas,
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: "Áreas de práctica — RR Lex Juristas" },
      { name: "description", content: "Más de 20 áreas del derecho: penal, familia, laboral, mercantil, civil, extranjería, administrativo y muchas más." },
      { property: "og:title", content: "Áreas de práctica — RR Lex Juristas" },
      { property: "og:description", content: "Todas nuestras especialidades jurídicas con subcategorías detalladas." },
      { property: "og:url", content: "https://rrlexjuristas.com/services/areas" },
    ],
    links: [{ rel: "canonical", href: "https://rrlexjuristas.com/services/areas" }],
  }))(ctx)),
});

function Areas() {
  const { tk } = useT();
  const services = useServicesList();
  const officeImg = useMedia("home.office.hero", heroOffice, "Despacho RR Lex Juristas");

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <span className="text-[10px] tracking-[0.4em] uppercase text-gold">{tk("servicios.2b66b4", "Áreas de Práctica")}</span>
          <h1 className="font-serif text-4xl md:text-5xl mt-4">
            {tk("servicios.fb3006", "Todas nuestras ")}
            <em className="not-italic gold-gradient">{tk("servicios.3522fa", "especialidades")}</em>
          </h1>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {services.map((s, i) => (
            <div key={s.slug} className="group rounded-2xl border border-gold/15 bg-card/30 p-8 hover:border-gold/60 hover:bg-card/60 transition-colors flex flex-col">
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h2 className="font-serif text-2xl mt-3 group-hover:text-gold transition-colors">{s.name}</h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>

              {s.subcategories && s.subcategories.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gold/15">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-4">
                    {tk("servicios.subcategorias", "Incluye")}
                  </div>
                  <ul className="space-y-3">
                    {s.subcategories.map((sub) => (
                      <li key={sub.name} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold shrink-0" aria-hidden />
                        <div>
                          <div className="text-sm font-medium text-foreground">{sub.name}</div>
                          <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">{sub.desc}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold/80 group-hover:text-gold">
                {tk("servicios.2af080", "Consultar")} <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section
        className="border-t border-gold/20 relative bg-cover bg-center"
        style={{ backgroundImage: `url(${officeImg.url})` }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="max-w-5xl mx-auto px-6 py-20 text-center relative z-10">
          <h2 className="font-serif text-4xl md:text-5xl text-white">{tk("servicios.876db8", "¿No encuentra su caso?")}</h2>
          <p className="mt-5 text-white/80">
            {tk("servicios.5c075e", "Trabajamos también con asuntos complejos y multidisciplinares. Escríbanos y le orientaremos.")}
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-full bg-gold text-primary-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors">
            {tk("servicios.9263ab", "Solicitar consulta")}
          </Link>
        </div>
      </section>
    </Layout>
  );
}
